import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useLazyQuery, useMutation } from '@apollo/client';

import CreditCardIcon from '@mui/icons-material/CreditCard';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import Loading from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import ELEMENT from 'common/element';

import { activeTeamState } from 'components/account/account.state';
import { ACCOUNT, UPDATE_CREDIT_CARD } from './queries';
import visa from './icons/VISA.png';
import americanExpress from './icons/AEX.png';
import discover from './icons/DISCOVER.png';
import masterCard from './icons/MASTER_CARD.png';
import { AccountDetails, CardIcons, CreditCardData } from './types';

import './styles.scss';

const CreditCardDialog = lazy(() => import('components/common/CreditCardDialog/CreditCardDialog'));

const cardIcons: CardIcons = {
  Visa: visa,
  'American Express': americanExpress,
  MasterCard: masterCard,
  Discover: discover,
};

const UpdateCreditCardPage: FC = () => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const { emailAddress } = useRecoilValue(activeTeamState);

  const showError = (): void => {
    setPageAlertConfig({
      message: 'There was an error while updating credit card info',
      variant: PageAlertVariant.ERROR,
      autoHide: true,
    });
  };

  const showSuccess = (): void => {
    setPageAlertConfig({
      message: 'Credit card info has been updated',
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
    });
  };

  const [loadAccount, { data, loading: accountLoading }] = useLazyQuery(ACCOUNT, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const [updateCreditCard] = useMutation(UPDATE_CREDIT_CARD, {
    onCompleted: () => {
      showSuccess();
      loadAccount();
    },
    onError: () => {
      showError();
      setLoading(false);
    },
  });

  const hasCreditCard = data?.account.hasCreditCard;
  const canChangeBillingPlan = data?.account.canChangeBillingPlan;
  const accountDetails: AccountDetails = {
    brand: data?.account.cardInformation?.brand || '',
    lastFour: data?.account.cardInformation?.lastFour || '****',
  };

  const onOpenDialogEdit = (): void => {
    setDialogOpen(true);
  };

  const onCancel = (): void => {
    setDialogOpen(false);
  };

  const onSubmit = (stripeToken: string, billingInformation: CreditCardData): void => {
    setLoading(true);
    updateCreditCard({
      variables: {
        stripeToken,
        billingInformation,
      },
    });
  };
  const pageName = `${hasCreditCard ? 'Update' : 'Add'} Credit Card`;

  const loadingState = loading || accountLoading;

  if (loadingState) {
    return <Loading />;
  }

  const creditCardBody = hasCreditCard ? (
    <div className="CreditCardPage__Card">
      <div className="CreditCardPage__CreditCardItem">
        {cardIcons[accountDetails.brand] ? <img alt="" src={cardIcons[accountDetails.brand]} /> : <CreditCardIcon />}
        <div className="CreditCardPage__CardNumber">{`Ends in ${accountDetails?.lastFour}`}</div>
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={onOpenDialogEdit}
          testId={ELEMENT.creditCardPageEditCreditCardButton}
        >
          Edit
        </Button>
      </div>
    </div>
  ) : (
    <>
      <div className="CreditCardPage__Card">
        <div className="CreditCardPage__NullMessage">No credit card on file</div>
      </div>
      <Button
        variant={ButtonVariant.PRIMARY}
        leftIcon={<CreditCardIcon />}
        onClick={onOpenDialogEdit}
        testId={ELEMENT.creditCardPageAddCreditCardButton}
      >
        Add Credit Card
      </Button>
    </>
  );

  const cannotChangeCreditCardMessage = (
    <div>
      Only the account owner can update the credit card. To update your credit card, please sign out and then sign back
      in as {emailAddress}.
    </div>
  );

  return (
    <Page docTitle="Credit Card">
      <PageHeader title="Credit Card" />
      {canChangeBillingPlan ? creditCardBody : cannotChangeCreditCardMessage}
      <Suspense fallback={<div>Loading...</div>}>
        <CreditCardDialog
          dialogOpen={dialogOpen}
          dialogTitle={pageName}
          onSubmit={onSubmit}
          onClose={onCancel}
          loading={loadingState}
        />
      </Suspense>
    </Page>
  );
};

export default UpdateCreditCardPage;
